import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VoiceService, VoiceCall } from '../../../../core/services/voice.service';
import { interval, Subscription } from 'rxjs';

@Component({
    selector: 'app-active-call',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    template: `
        @if (activeCall$ | async; as call) {
            <div class="active-call-bubble">
                <!-- Header -->
                <div class="bubble-header">
                    <div class="connected-dot"></div>
                    <span class="header-text">{{ call.direction === 'outgoing' ? 'Calling' : 'On Call' }}</span>
                    <span class="call-duration">{{ callDuration }}</span>
                </div>
                
                <!-- Caller info -->
                <div class="caller-info">
                    <mat-icon class="caller-icon">{{ call.direction === 'outgoing' ? 'phone_forwarded' : 'phone_in_talk' }}</mat-icon>
                    <span class="caller-number">{{ formatPhoneNumber(call.direction === 'outgoing' ? call.to : call.from) }}</span>
                </div>

                <!-- DTMF Dialpad -->
                @if (showDialpad) {
                    <div class="dialpad-section">
                        <div class="dtmf-display">{{ dtmfInput || '&nbsp;' }}</div>
                        <div class="dialpad-grid">
                            @for (key of dialpadKeys; track key.digit) {
                                <button class="dialpad-key" (click)="onDialpadPress(key.digit)">
                                    <span class="key-digit">{{ key.digit }}</span>
                                    @if (key.letters) {
                                        <span class="key-letters">{{ key.letters }}</span>
                                    }
                                </button>
                            }
                        </div>
                    </div>
                }
                
                <!-- Action buttons -->
                <div class="call-actions">
                    <button 
                        mat-mini-fab 
                        [class.muted]="call.isMuted"
                        (click)="toggleMute()"
                        [matTooltip]="call.isMuted ? 'Unmute' : 'Mute'">
                        <mat-icon>{{ call.isMuted ? 'mic_off' : 'mic' }}</mat-icon>
                    </button>

                    <button 
                        mat-mini-fab 
                        [class.active-action]="showDialpad"
                        (click)="showDialpad = !showDialpad"
                        matTooltip="Keypad">
                        <mat-icon>dialpad</mat-icon>
                    </button>
                    
                    <button 
                        mat-mini-fab 
                        color="warn"
                        class="hangup-btn"
                        (click)="hangup()"
                        matTooltip="End Call">
                        <mat-icon>call_end</mat-icon>
                    </button>
                </div>
            </div>
        }
    `,
    styles: [`
        .active-call-bubble {
            position: fixed;
            bottom: 24px;
            right: 24px;
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
            color: white;
            border-radius: 12px;
            padding: 16px;
            min-width: 280px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
            z-index: 1000;
            animation: slideIn 0.3s ease-out;
        }
        
        @keyframes slideIn {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        
        .bubble-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
        }
        
        .connected-dot {
            width: 10px;
            height: 10px;
            background: #8eff8e;
            border-radius: 50%;
            animation: pulse-connected 1.5s ease-in-out infinite;
        }
        
        @keyframes pulse-connected {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
        
        .header-text {
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.9;
        }
        
        .call-duration {
            margin-left: auto;
            font-size: 14px;
            font-family: 'Roboto Mono', monospace;
            background: rgba(255, 255, 255, 0.2);
            padding: 2px 8px;
            border-radius: 4px;
        }
        
        .caller-info {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
        }
        
        .caller-icon {
            font-size: 28px;
            width: 28px;
            height: 28px;
            opacity: 0.9;
        }
        
        .caller-number {
            font-size: 16px;
            font-weight: 500;
            font-family: 'Roboto Mono', monospace;
        }
        
        .call-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
        }
        
        .dialpad-section {
            margin-bottom: 12px;
        }
        
        .dtmf-display {
            text-align: center;
            font-family: 'Roboto Mono', monospace;
            font-size: 14px;
            letter-spacing: 3px;
            min-height: 24px;
            color: rgba(255, 255, 255, 0.85);
            margin-bottom: 8px;
        }
        
        .dialpad-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
            max-width: 210px;
            margin: 0 auto;
        }
        
        .dialpad-key {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 42px;
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.15);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: white;
            cursor: pointer;
            transition: background 0.15s;
        }
        
        .dialpad-key:hover {
            background: rgba(255, 255, 255, 0.25);
        }
        
        .dialpad-key:active {
            background: rgba(255, 255, 255, 0.35);
        }
        
        .key-digit {
            font-size: 16px;
            font-weight: 600;
            line-height: 1;
        }
        
        .key-letters {
            font-size: 8px;
            letter-spacing: 1.5px;
            opacity: 0.7;
            margin-top: 1px;
        }
        
        .call-actions button {
            background: rgba(255, 255, 255, 0.2) !important;
            color: white;
        }
        
        .call-actions button.muted,
        .call-actions button.active-action {
            background: rgba(255, 255, 255, 0.4) !important;
        }
        
        .hangup-btn {
            background: #f44336 !important;
        }
        
        /* Mobile responsive */
        @media (max-width: 600px) {
            .active-call-bubble {
                bottom: 16px;
                right: 16px;
                left: 16px;
                min-width: auto;
            }
        }
    `]
})
export class ActiveCallComponent implements OnDestroy {
    private voiceService = inject(VoiceService);
    
    activeCall$ = this.voiceService.activeCall$;
    callDuration = '00:00';
    showDialpad = false;
    dtmfInput = '';
    dialpadKeys: { digit: string; letters: string }[] = [
        { digit: '1', letters: '' },
        { digit: '2', letters: 'ABC' },
        { digit: '3', letters: 'DEF' },
        { digit: '4', letters: 'GHI' },
        { digit: '5', letters: 'JKL' },
        { digit: '6', letters: 'MNO' },
        { digit: '7', letters: 'PQRS' },
        { digit: '8', letters: 'TUV' },
        { digit: '9', letters: 'WXYZ' },
        { digit: '*', letters: '' },
        { digit: '0', letters: '+' },
        { digit: '#', letters: '' },
    ];
    
    private durationSubscription: Subscription | null = null;
    private callStartTime: Date | null = null;
    
    constructor() {
        // Subscribe to active call to track duration
        this.activeCall$.subscribe(call => {
            if (call && call.status === 'connected') {
                this.startDurationTimer(call);
            } else {
                this.stopDurationTimer();
                this.showDialpad = false;
                this.dtmfInput = '';
            }
        });
    }
    
    ngOnDestroy(): void {
        this.stopDurationTimer();
    }
    
    private startDurationTimer(call: VoiceCall): void {
        this.stopDurationTimer();
        this.callStartTime = call.startTime || new Date();
        
        this.durationSubscription = interval(1000).subscribe(() => {
            this.updateDuration();
        });
        this.updateDuration();
    }
    
    private stopDurationTimer(): void {
        if (this.durationSubscription) {
            this.durationSubscription.unsubscribe();
            this.durationSubscription = null;
        }
        this.callDuration = '00:00';
        this.callStartTime = null;
    }
    
    private updateDuration(): void {
        if (!this.callStartTime) return;
        
        const now = new Date();
        const diff = Math.floor((now.getTime() - this.callStartTime.getTime()) / 1000);
        
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;
        
        this.callDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    onDialpadPress(digit: string): void {
        this.voiceService.sendDtmf(digit);
        this.dtmfInput += digit;
    }

    toggleMute(): void {
        this.voiceService.toggleMute();
    }
    
    hangup(): void {
        this.showDialpad = false;
        this.dtmfInput = '';
        this.voiceService.hangupCall();
    }
    
    formatPhoneNumber(phone: string): string {
        if (!phone) return 'Unknown';
        
        const digits = phone.replace(/\D/g, '');
        
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11 && digits.startsWith('1')) {
            return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        
        return phone;
    }
}
